import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { arraysEqual, parseToDate, _ } from '~app/shared/utils';
import { combineLatest, Subject, Subscription, Observable } from 'rxjs';
import { LoadingConfig } from '~app/components/qm-directives/loading.directive';
import { TagMetadata } from './measurements/neve-measurements-tag.interface';
import { TagState } from './measurements/neve-measurements.interface';
import { NeveMeasurementsService } from './measurements/neve-measurements.service';
import { map, startWith, take } from 'rxjs/operators';
import { getFullTag } from './measurements/neve-measurements-tag.service';
import { TagPair, ValmetDnaTagConfig, ValmetDnaTransformedValue } from '~app/shared/map/layer/layer-definitions/T14';
import { differenceInMinutes } from 'date-fns';
import { NgbdSortableHeaderDirective, SortEvent } from '~app/components/qm-directives/sortable-header.directive';
import * as lodash from 'lodash';

type TableRow = {
    description: string,
    formattedValue?: string,
    valmetDnaValue?: ValmetDnaTransformedValue,
    valmetDnaMetadata: TagMetadata
};

interface TableData {
  rows: TableRow[],
  totalValueSum: string
}

type TagpairState = {
  tagpair: TagPair,
  state: TagState,
  config: ValmetDnaTagConfig
};

@Component({
  selector: 'qm-neve-measurements-table',
  templateUrl: './neve-measurements-table.component.html',
  styleUrls: ['./neve-measurements-table.component.scss']
})
export class NeveMeasurementsTableComponent implements OnInit, OnDestroy {
  public tableData$: Observable<TableData>;
  public loading: boolean;
  public loadingConfig: LoadingConfig = {
    timeout: 0,
    messages: {
      timeout: _('Please wait a moment, fetching data from Valmet DNA'),
      empty: _('Measurement data is currently not available'),
    }
  };
  @Input('valmetDnaTags')
  public tagConfigs: ValmetDnaTagConfig[] = [];

  @ViewChildren(NgbdSortableHeaderDirective) headers: QueryList<NgbdSortableHeaderDirective>;

  private tagpairs: TagPair[];
  private readonly subs: Subscription[] = [];
  private readonly sortEvents = new Subject<SortEvent>();
  private readonly sortEvents$: Observable<SortEvent>;

  constructor(private readonly measurements: NeveMeasurementsService) {
    this.sortEvents$ = this.sortEvents.asObservable().pipe(
      startWith({
        column: '',
        direction: ''
      })
    );
  }

  onSort(event: SortEvent) {
    this.sortEvents.next(event);
  }

  ngOnDestroy(): void {
    for(const sub of this.subs) {
      sub.unsubscribe();
    }
  }

  async ngOnInit() {
    this.tagpairs = this.tagConfigs.map(config => config.tagpair);
    this.loading = true;
    // for now only starts querying once component inits
    this.measurements.init(this.tagpairs);
    const allTagStates = this.tagpairs.map(tagpair =>
      this.measurements.tagState$(tagpair)
        .pipe(
          map(state => {
            return {
              tagpair,
              state,
              config: this.tagConfigs.find(config => arraysEqual(config.tagpair, tagpair))
            } as TagpairState;
          }),
        )
    );
    const latestTagStates = combineLatest(allTagStates);
    const unsortedRows$ = latestTagStates.pipe(
      map(tagpairStates => ({
        unsortedRows: tagpairStates.map(tagpairState => this.getTableRow(tagpairState)),
        tagpairStates
      }))
    );
    const sortedRows$ = combineLatest([unsortedRows$, this.sortEvents$]).pipe(
      map((inputs) => {
        const compare = (v1: string | number, v2: string | number) => v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
        const {unsortedRows, tagpairStates} = inputs[0];
        const {column, direction} = inputs[1];
        const sortedRows = [...unsortedRows].sort((a, b) => {
          const aColumn = lodash.get(a, column);
          const bColumn = lodash.get(b, column);
          const res = compare(aColumn, bColumn);
          return direction === 'asc' ? res : -res;
        });
        return {
          tagpairStates,
          sortedRows
        };
      })
    );
    this.tableData$ = sortedRows$.pipe(
      map(obj => {
        const {sortedRows, tagpairStates} = obj;
        const totalValueSum = this.calculateTotalValueSum(tagpairStates);
        return {
          rows: sortedRows,
          totalValueSum: totalValueSum ? totalValueSum.toFixed(0) : undefined
        };
      })
    );

    this.subs.push(latestTagStates.pipe(take(1)).subscribe({
      complete: () => {
        this.loading = false;
      }
    }));
  }

  public valueIsZero(value: ValmetDnaTransformedValue) {
    return value?.numericValue === 0;
  }

  public timestampAgeInMinutes(value: Date) {
    const diff = differenceInMinutes(new Date(), parseToDate(value));
    if(diff === 0) {
      return '< 1';
    }
    return diff.toString();
  }

  private transformState(tps: TagpairState) {
    return lodash.merge({}, tps, {
      state: {
        transformedMeasurements: tps.state.measurements.map(m => tps.config.valueTransformer(m))
      }
    });
  }

  private getLastValue<T>(values: T[]) {
    if (values.length === 0) {
      return undefined;
    }

    return values[values.length - 1];
  }

  private calculateTotalValueSum(tagpairStates: TagpairState[]) {
    const allMeasurements = tagpairStates
      .filter(tps => tps.state.status === 'loaded' && !!tps.state.measurements)
      .map(tps => this.transformState(tps))
      .map(tps => this.getLastValue(tps.state.transformedMeasurements))
      .flat();
      return this.calculateAggregateSum(allMeasurements);
  }

  private getTableRow(tagpairState: TagpairState): TableRow {
    const description = getFullTag(tagpairState.tagpair);
    if(!tagpairState.state.measurements) {
      return {
        description,
        formattedValue: undefined,
        valmetDnaValue: undefined,
        valmetDnaMetadata: tagpairState.state.metadata
      };
    }
    const { state } = this.transformState(tagpairState);
    const hasValue = state.transformedMeasurements.length > 0;
    const lastTrendValue = hasValue ? this.getLastValue(state.transformedMeasurements) : null;

    return {
      description,
      formattedValue: hasValue ? (lastTrendValue.numericValue).toFixed(2) : undefined,
      valmetDnaValue: lastTrendValue,
      valmetDnaMetadata: state.metadata
    };
  }

  private calculateAggregateSum(values: ValmetDnaTransformedValue[]): number | undefined {
    const getTruthyValues = (possiblyNullishValues: ValmetDnaTransformedValue[]) => {
      return possiblyNullishValues
        .map(result => !!result?.value ? result : null)
        .filter(value => value !== null)
      ;
    };

      // ensure null values do not affect the aggregate sum
    const truthyValues = getTruthyValues(values);
    return truthyValues.length > 0 ?
      truthyValues
        .map(value => value.numericValue)
        .reduce((prev, curr) => prev + curr)
      : undefined;
  }

}
